import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { ClientMessageType } from '@shared/types/messages';
import { BUFF_EXPIRE_FX_BY_ACTIVE } from '@shared/data/spells';
import { getItemTemplateByVisualId } from '@shared/data/items';
import { getParticleEffect } from '@shared/data/particleEffects';
import { INVIS_PIPELINE_KEY } from '../../sprites/InvisibilityPipeline';

/**
 * VFX de emitters pegados a entidades (sincronizados por estado del server): auras toggle
 * (syncAuraEmittersFor / broadcastAuraToggle), visuales de pérdida de movilidad (raíces de
 * inmovilizar + tint pétreo de paralizar, syncImpairVisualsFor) y FX de items equipados
 * (syncEquippedVfxFor). Extraído de GameScene (god-object). Los Maps de handles y los statics
 * (ROOTS_FX_ID/PARALYZE_TINT/SPAWN_PROTECTION_GLOW) viven en GameScene (host) y se acceden vía
 * `this.scene.*` / `GameScene.*`. Ver docs/SPELLS.md (auras), docs/CC_IMMOBILIZE_PARALYZE.md.
 */
export class EntityVfxController {
  constructor(private scene: GameScene) {}

  public broadcastAuraToggle(fxId: string): void {
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: ClientMessageType.TOGGLE_AURA_FX,
      fxId,
    } as never);
  }





  /**
   * Sincroniza el set de auras VFX activas sobre un entity con el state del
   * server (PlayerState.auraFxIds). Llamado desde addOrUpdatePlayer cada vez
   * que llega un update. Hace un diff: spawn de las nuevas, stop de las que
   * desaparecen. Cap implícito de 4 por player (enforced server-side).
   * `suppressExpireFx`: no disparar los one-shot de expiración al parar
   * emitters (usado al OCULTAR por navegar, que no es una expiración real).
   */
  public syncAuraEmittersFor(
    entityId: string,
    desiredFxIds: readonly string[],
    suppressExpireFx: boolean = false
  ): void {
    const mgr = this.scene.particleFx.ensureParticleManager();
    let active = this.scene.activeAuraEmitters.get(entityId);
    if (!active) {
      active = new Map();
      this.scene.activeAuraEmitters.set(entityId, active);
    }

    // 1) Spawn de los nuevos
    const desired = new Set(desiredFxIds);
    for (const fxId of desired) {
      if (active.has(fxId)) continue;
      // Decidir slot según tag 'feet'
      const def = getParticleEffect(fxId);
      const slot = def && (def.tags ?? []).includes('feet') ? 'feet' : 'center';
      const handle = mgr.spawn(fxId, {
        kind: 'entity',
        entityId,
        slot,
      }, { ownerId: entityId, priority: 'normal' });
      if (handle) {
        active.set(fxId, handle);
        console.log(`[Aura] spawn ${fxId} en ${entityId} (motor v2)`);
      } else {
        // Fallback v1: motor v1 sigue al local player con activeParticleEmitters;
        // para auras de OTROS players el v1 no tiene anchor — saltamos por ahora.
        // (Suficiente para F4 MVP — solo auras compatibles con v2 native sync.)
        console.warn(`[Aura] ${fxId} no compatible con motor v2 — sync skipped para ${entityId}`);
      }
    }

    // 2) Stop de las que ya no están
    for (const [fxId, handle] of active.entries()) {
      if (!desired.has(fxId)) {
        mgr.stop(handle);
        active.delete(fxId);
        console.log(`[Aura] stop ${fxId} en ${entityId}`);
        // FX de buffs — momento "expiración": si el activeFxId que se fue tiene un
        // expireFxId emparejado en el catálogo, disparar el one-shot sobre la entidad.
        // (El server sacó el id de auraFxIds al expirar el buff; ver docs/PARTICLES.md.)
        const expireFxId = suppressExpireFx ? undefined : BUFF_EXPIRE_FX_BY_ACTIVE.get(fxId);
        if (expireFxId) {
          mgr.spawn(expireFxId, { kind: 'entity', entityId, slot: 'center' });
        }
      }
    }
  }

  public clearAuraEmittersFor(entityId: string): void {
    const active = this.scene.activeAuraEmitters.get(entityId);
    if (!active) return;
    const mgr = this.scene.particleManager;
    if (mgr) {
      for (const handle of active.values()) mgr.stop(handle);
    }
    this.scene.activeAuraEmitters.delete(entityId);
  }

  /**
   * Sincroniza los visuales del estado de pérdida de movilidad de una entidad:
   * raíces verdes persistentes a los pies si está inmovilizada (1), tint pétreo
   * sobre todo el personaje si está paralizada (2). Idempotente; llamado en cada
   * update de la entidad. Ver docs/CC_IMMOBILIZE_PARALYZE.md.
   */
  public syncImpairVisualsFor(entityId: string, impairState: number): void {
    const mgr = this.scene.particleFx.ensureParticleManager();

    // Raíces (inmovilizado): emitter persistente a los pies.
    const hasRoots = this.scene.activeRootsEmitters.has(entityId);
    if (impairState === 1 && !hasRoots) {
      const handle = mgr.spawn(
        GameScene.ROOTS_FX_ID,
        { kind: 'entity', entityId, slot: 'feet' },
        { ownerId: entityId, priority: 'normal' }
      );
      if (handle) this.scene.activeRootsEmitters.set(entityId, handle);
    } else if (impairState !== 1 && hasRoots) {
      const h = this.scene.activeRootsEmitters.get(entityId);
      if (h) mgr.stop(h);
      this.scene.activeRootsEmitters.delete(entityId);
    }

    // Tint pétreo (paralizado): se reaplica cada update (robusto ante cambios de
    // textura del sprite) y se limpia una vez al dejar de estar paralizado.
    if (impairState === 2) {
      this.applyImpairTint(entityId, true);
      this.scene.paralyzeTintedEntities.add(entityId);
    } else if (this.scene.paralyzeTintedEntities.has(entityId)) {
      this.applyImpairTint(entityId, false);
      this.scene.paralyzeTintedEntities.delete(entityId);
    }
  }

  /**
   * Sincroniza el render de invisibilidad de una entidad con su `invisibleState`
   * (0 visible / 1 invisible / 2 revelado). Decide POR OBSERVADOR cuánto se ve:
   * uno mismo se ve semi-transparente, un aliado de party o teniendo `seeInvisible`
   * lo ve tenue, un desconocido casi no lo ve (y sin nombre), y un revelado lo ven
   * todos. Aplica el cloak (InvisibilityPipeline) + alpha + visibilidad del nombre.
   * Idempotente; llamado en cada update de la entidad. Ver docs/INVISIBILITY.md.
   */
  public syncInvisibilityVisualsFor(entityId: string, invisibleState: number, isPlayer: boolean): void {
    const wasInvisible = this.scene.invisibleEntities.has(entityId);

    if (invisibleState === 0) {
      if (wasInvisible) this.clearInvisibilityVisualsFor(entityId);
      return;
    }

    // Relación con el observador local → alpha del cuerpo + alpha/visibilidad del nombre.
    const localId = this.scene.localSessionId;
    const isSelf = isPlayer && entityId === localId;
    const isParty = isPlayer && this.scene.partyController?.isPartyMember(entityId) === true;
    const localState = localId ? this.scene.room?.state.players.get(localId) : undefined;
    const canSeeInvisible = !!(localState as unknown as { canSeeInvisible?: boolean })?.canSeeInvisible;

    let bodyAlpha: number;
    let nameAlpha: number; // <0 = ocultar el nombre
    if (invisibleState === 2) {
      // Revelado por detección: lo ven todos (figura tenue que titila con el pulso).
      bodyAlpha = 0.6;
      nameAlpha = 0.7;
    } else if (isSelf) {
      // Yo mismo: me veo semi-transparente para saber que estoy oculto; veo mi nombre.
      bodyAlpha = 0.35;
      nameAlpha = 1.0;
    } else if (isParty || canSeeInvisible) {
      // Aliado de party o con "ver invisibles": lo veo tenue, con el nombre poco opaco.
      bodyAlpha = 0.5;
      nameAlpha = 0.55;
    } else {
      // Desconocido: casi invisible (el pulso lo "exhibe" cada 2-5s) y SIN nombre.
      bodyAlpha = 0.03;
      nameAlpha = -1;
    }

    this.applyInvisibilityCloak(entityId, isPlayer, bodyAlpha, nameAlpha);
    this.scene.invisibleEntities.add(entityId);
  }

  /** Aplica el cloak (pipeline + alpha) sobre los sprites y ajusta el nombre. */
  private applyInvisibilityCloak(
    entityId: string,
    isPlayer: boolean,
    bodyAlpha: number,
    nameAlpha: number
  ): void {
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return;
    const r = render as unknown as Record<string, Phaser.GameObjects.Sprite | null | undefined>;
    const sprites = [r.sprite, r.head, r.helm, r.boots, r.weapon, r.shield];
    for (const s of sprites) {
      if (!s) continue;
      s.setAlpha(bodyAlpha);
      if (typeof s.setPipeline === 'function') s.setPipeline(INVIS_PIPELINE_KEY);
    }
    // Nombre: sólo lo manejamos para players (los labels de NPC dependen del hover).
    if (isPlayer) {
      const label = (render as unknown as { label?: Phaser.GameObjects.Text }).label;
      if (label) {
        if (nameAlpha < 0) {
          label.setVisible(false);
        } else {
          label.setVisible(true).setAlpha(nameAlpha);
        }
      }
    }
  }

  /** Restaura el render normal de una entidad que dejó de ser invisible. */
  public clearInvisibilityVisualsFor(entityId: string): void {
    if (!this.scene.invisibleEntities.has(entityId)) return;
    this.scene.invisibleEntities.delete(entityId);
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return;
    const r = render as unknown as Record<string, Phaser.GameObjects.Sprite | null | undefined>;
    const sprites = [r.sprite, r.head, r.helm, r.boots, r.weapon, r.shield];
    for (const s of sprites) {
      if (!s) continue;
      s.setAlpha(1);
      if (typeof s.resetPipeline === 'function') s.resetPipeline();
    }
    const label = (render as unknown as { label?: Phaser.GameObjects.Text }).label;
    if (label) label.setVisible(true).setAlpha(1);
  }

  /** Aplica o limpia el tint pétreo sobre todos los sprites de una entidad. */
  public applyImpairTint(entityId: string, on: boolean): void {
    const render = this.scene.playerObjects.get(entityId) ?? this.scene.npcObjects.get(entityId);
    if (!render) return;
    const r = render as unknown as Record<string, Phaser.GameObjects.Sprite | null | undefined>;
    const sprites = [r.sprite, r.head, r.helm, r.boots, r.weapon, r.shield];
    for (const s of sprites) {
      if (!s || typeof s.setTint !== 'function') continue;
      if (on) s.setTint(GameScene.PARALYZE_TINT);
      else s.clearTint();
    }
  }

  /** Limpia los visuales de impair de una entidad (al despawnear / morir). */
  public clearImpairVisualsFor(entityId: string): void {
    const h = this.scene.activeRootsEmitters.get(entityId);
    if (h) this.scene.particleManager?.stop(h);
    this.scene.activeRootsEmitters.delete(entityId);
    if (this.scene.paralyzeTintedEntities.has(entityId)) {
      this.applyImpairTint(entityId, false);
      this.scene.paralyzeTintedEntities.delete(entityId);
    }
  }

  /**
   * F5 — Sincroniza VFX de items equipados con los visualIds del PlayerState.
   * Hace lookup del template canónico por slot+visualId, lee el vfx.equipped
   * y mantiene los emitters via diff. Cap implícito: cada item tiene su
   * propio listado, sin tope global (el budget del ParticleManager protege).
   */
  public syncEquippedVfxFor(
    entityId: string,
    visualIds: { weaponId?: number; bodyId?: number; helmId?: number; shieldId?: number; bootId?: number }
  ): void {
    const mgr = this.scene.particleFx.ensureParticleManager();
    let active = this.scene.activeEquippedEmitters.get(entityId);
    if (!active) {
      active = new Map();
      this.scene.activeEquippedEmitters.set(entityId, active);
    }

    // 1) Calcular el set deseado: para cada slot equipado, buscar template
    //    con vfx y armar entries `${slot}|${fxId}` (key compuesta para dup-safe).
    const desired = new Map<string, { fxId: string; slot: string }>();
    const slotsToCheck: Array<[keyof typeof visualIds, 'weapon' | 'body' | 'head' | 'shield' | 'boots']> = [
      ['weaponId', 'weapon'],
      ['bodyId', 'body'],
      ['helmId', 'head'],
      ['shieldId', 'shield'],
      ['bootId', 'boots'],
    ];
    for (const [visualKey, slotKey] of slotsToCheck) {
      const visualId = visualIds[visualKey];
      if (!visualId) continue;
      const tpl = getItemTemplateByVisualId(slotKey, visualId);
      if (!tpl?.vfx?.equipped) continue;
      for (const entry of tpl.vfx.equipped) {
        const key = `${slotKey}|${entry.fxId}|${entry.slot}`;
        desired.set(key, { fxId: entry.fxId, slot: entry.slot });
      }
    }

    // 2) Spawn de los nuevos
    for (const [key, entry] of desired.entries()) {
      if (active.has(key)) continue;
      const handle = mgr.spawn(
        entry.fxId,
        { kind: 'entity', entityId, slot: entry.slot as never },
        { ownerId: entityId, priority: 'low' }
      );
      if (handle) {
        active.set(key, handle);
      }
    }

    // 3) Stop de los que ya no están equipados
    for (const [key, handle] of active.entries()) {
      if (!desired.has(key)) {
        mgr.stop(handle);
        active.delete(key);
      }
    }
  }

  public clearEquippedVfxFor(entityId: string): void {
    const active = this.scene.activeEquippedEmitters.get(entityId);
    if (!active) return;
    const mgr = this.scene.particleManager;
    if (mgr) for (const handle of active.values()) mgr.stop(handle);
    this.scene.activeEquippedEmitters.delete(entityId);
  }

}
